/**
 * Backend Analytics Adapter
 *
 * Sends analytics events to our own backend API instead of third-party services.
 * Privacy-first approach - no cookies required, no user tracking.
 *
 * NOTE: Uses the same session ID as the cart (stork_session_id) to enable
 * linking analytics events to user journeys across cart → checkout → purchase.
 */

import { env } from '../env';
import { getSessionId } from '@/hooks/useSession';

export interface AnalyticsEvent {
    eventName: string;
    eventCategory: 'page' | 'ecommerce' | 'user' | 'engagement' | 'error';
    timestamp: string;
    sessionId: string;
    pagePath?: string;
    pageTitle?: string;
    referrer?: string;
    deviceType?: 'desktop' | 'tablet' | 'mobile';
    viewportWidth?: number;
    properties?: Record<string, unknown>;
}

interface BatchResponse {
    received: number;
    processed: number;
    errors?: Array<{ index: number; error: string }>;
    timestamp: string;
}

/**
 * Backend Analytics Adapter
 *
 * Batches events and sends them to our backend API.
 * Uses Beacon API for reliable delivery on page close.
 */
export class BackendAdapter {
    private apiUrl: string;
    private eventQueue: AnalyticsEvent[] = [];
    private flushTimer: ReturnType<typeof setTimeout> | null = null;

    // Configuration
    private readonly BATCH_SIZE = 10;
    private readonly FLUSH_INTERVAL_MS = 5000; // 5 seconds
    private readonly MAX_QUEUE_SIZE = 50;

    constructor() {
        // API_URL already includes /api (e.g., http://localhost:5082/api)
        // Using "customer/events" instead of "analytics" to avoid ad-blocker interference
        this.apiUrl = `${env.API_URL}/customer/events`;

        // Flush on page close/hide
        if (typeof window !== 'undefined') {
            window.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') {
                    this.flushWithBeacon();
                }
            });
            window.addEventListener('pagehide', () => this.flushWithBeacon());
        }
    }

    /**
     * Get session ID from shared session management (useSession.ts)
     * Uses the same session ID as the cart for unified tracking
     */
    private getSessionId(): string {
        return getSessionId();
    }

    /**
     * Get device type based on viewport width
     */
    private getDeviceType(): 'desktop' | 'tablet' | 'mobile' {
        if (typeof window === 'undefined') return 'desktop';
        const width = window.innerWidth;
        if (width < 768) return 'mobile';
        if (width < 1024) return 'tablet';
        return 'desktop';
    }

    /**
     * Track an event
     */
    track(
        eventName: string,
        category: AnalyticsEvent['eventCategory'],
        properties?: Record<string, unknown>
    ): void {
        const event: AnalyticsEvent = {
            eventName,
            eventCategory: category,
            timestamp: new Date().toISOString(),
            sessionId: this.getSessionId(),
            pagePath: typeof window !== 'undefined' ? window.location.pathname : undefined,
            pageTitle: typeof document !== 'undefined' ? document.title : undefined,
            referrer: typeof document !== 'undefined' ? document.referrer : undefined,
            deviceType: this.getDeviceType(),
            viewportWidth: typeof window !== 'undefined' ? window.innerWidth : undefined,
            properties,
        };

        this.eventQueue.push(event);

        // Prevent queue from growing too large
        if (this.eventQueue.length >= this.MAX_QUEUE_SIZE) {
            this.flush();
            return;
        }

        // Flush when batch size reached
        if (this.eventQueue.length >= this.BATCH_SIZE) {
            this.flush();
            return;
        }

        // Schedule flush if not already scheduled
        if (!this.flushTimer) {
            this.flushTimer = setTimeout(() => this.flush(), this.FLUSH_INTERVAL_MS);
        }
    }

    /**
     * Track an error event
     */
    trackError(
        errorMessage: string,
        properties?: Record<string, unknown>
    ): void {
        this.track('error', 'error', {
            errorMessage: errorMessage.substring(0, 500), // Limit length
            ...properties,
        });
    }

    /**
     * Flush events to backend using fetch
     */
    async flush(): Promise<void> {
        if (this.flushTimer) {
            clearTimeout(this.flushTimer);
            this.flushTimer = null;
        }

        if (this.eventQueue.length === 0) return;

        const events = this.eventQueue.splice(0, this.BATCH_SIZE);

        try {
            const response = await fetch(this.apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ events }),
            });

            if (!response.ok) {
                console.warn('[Analytics] Failed to send events:', response.status);
                // Put events back in queue for retry
                this.eventQueue.unshift(...events);
            } else {
                const result: BatchResponse = await response.json();
                if (result.errors && result.errors.length > 0) {
                    console.warn('[Analytics] Some events failed:', result.errors);
                }
            }
        } catch (error) {
            console.warn('[Analytics] Network error sending events:', error);
            // Put events back in queue for retry (limited)
            if (this.eventQueue.length < this.MAX_QUEUE_SIZE) {
                this.eventQueue.unshift(...events);
            }
        }
    }

    /**
     * Flush using Beacon API (for page close events)
     * Beacon is fire-and-forget, guaranteed to be sent even if page closes
     */
    private flushWithBeacon(): void {
        if (this.eventQueue.length === 0) return;

        const events = this.eventQueue.splice(0, this.MAX_QUEUE_SIZE);

        try {
            const blob = new Blob(
                [JSON.stringify({ events })],
                { type: 'application/json' }
            );
            navigator.sendBeacon(this.apiUrl, blob);
        } catch (error) {
            console.warn('[Analytics] Beacon send failed:', error);
        }
    }
}

// Singleton instance
export const backendAdapter = new BackendAdapter();
